import 'moment-timezone';

import moment from 'moment';

import { LegacyMetricPopupType } from 'webapp/enums/metric-mover';

// Pure helpers for the one-time legacy metric deletion popups (see services/metric-mover.ts).

const DATE_FORMAT = 'YYYY-MM-DD';

const LEGACY_POPUP_MAX_LISTED_SOURCES = 5;

export type LegacyPopupUserRole = 'owner' | 'admin' | 'data_source_owner';

export interface LegacyPopupSource {
	title: string;
	isOwnedByMe: unknown;
	isTypeOfDataset: unknown;
	sourceType?: {
		isSQLType: boolean;
		isSheets: boolean; // all Excel types too (framework DataSourceType::SHEET_SOURCES)
		isToken: boolean;
	};
}

export interface LegacyPopupCandidate {
	type: LegacyMetricPopupType;
	deletionDate: string;
}

function isLegacyPopupSource(source: LegacyPopupSource, type: LegacyMetricPopupType): boolean {
	const { sourceType } = source;

	// Dataset sources hold the new metrics; the popup lists the migrated legacy sources
	// (same exclusion as Source.isLegacyCustomSource).
	if (!sourceType || source.isTypeOfDataset) {
		return false;
	}

	if (type === LegacyMetricPopupType.PUSH_API) {
		return sourceType.isToken;
	}

	return sourceType.isSQLType || sourceType.isSheets;
}

// Account owners and admins see every matching source, other users only the ones they own.
export function getLegacyPopupSources<T extends LegacyPopupSource>(
	sources: T[],
	type: LegacyMetricPopupType,
	canSeeAllSources: boolean,
): T[] {
	return sources
		.filter((source) => isLegacyPopupSource(source, type))
		.filter((source) => canSeeAllSources || !!source.isOwnedByMe)
		.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
}

// Up to `max` names, then a last "and {N} more" line.
export function getLegacyPopupSourceLines(
	names: string[],
	max = LEGACY_POPUP_MAX_LISTED_SOURCES,
): string[] {
	if (names.length <= max) {
		return names;
	}

	return [...names.slice(0, max), `and ${names.length - max} more`];
}

export function parseSeenSpaceIds(value: unknown): number[] {
	if (typeof value !== 'string' && typeof value !== 'number') {
		return [];
	}

	return String(value)
		.split(',')
		.map((id) => Number(id.trim()))
		.filter((id) => Number.isInteger(id) && id > 0);
}

export function addSeenSpaceId(value: unknown, spaceId: number): string {
	const spaceIds = parseSeenSpaceIds(value);

	if (!spaceIds.includes(spaceId)) {
		spaceIds.push(spaceId);
	}

	return spaceIds.join(',');
}

export function isValidLegacyDeletionDate(date: string | null | undefined): date is string {
	return !!date && moment(date, DATE_FORMAT, true).isValid();
}

// Today's date as 'YYYY-MM-DD' in the given timezone.
export function getTodayInTimezone(timezone: string): string {
	return moment().tz(timezone).format(DATE_FORMAT);
}

// Both dates are 'YYYY-MM-DD'. Negative when the deletion date has passed.
export function getDaysUntilDeletion(deletionDate: string, today: string): number {
	return moment(deletionDate, DATE_FORMAT, true).diff(moment(today, DATE_FORMAT, true), 'days');
}

export function isBeforeOrOnDeletionDate(deletionDate: string, today: string): boolean {
	return getDaysUntilDeletion(deletionDate, today) >= 0;
}

// The candidate with the earliest deletion date; spreadsheet/SQL wins a tie.
export function pickLegacyPopupCandidate(
	candidates: LegacyPopupCandidate[],
): LegacyPopupCandidate | null {
	return (
		[...candidates].sort((a, b) => {
			const byDate = getDaysUntilDeletion(a.deletionDate, b.deletionDate);
			if (byDate !== 0) {
				return byDate;
			}
			return a.type === LegacyMetricPopupType.SPREADSHEET_SQL ? -1 : 1;
		})[0] ?? null
	);
}

// 'YYYY-MM-DD' -> 'November 3, 2026'
export function formatLegacyDeletionDate(deletionDate: string): string {
	return moment(deletionDate, DATE_FORMAT, true).format('MMMM D, YYYY');
}

export function getLegacyPopupUserRole(isOwner: boolean, isAdmin: boolean): LegacyPopupUserRole {
	if (isOwner) {
		return 'owner';
	}
	return isAdmin ? 'admin' : 'data_source_owner';
}
